import { type ReactNode, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useDispatch, useStore } from 'react-redux';
import RedBox from 'redbox-react';

import type { AppState } from '../../reducer';
import { getCurrentAuthRedirectPath, normalizeAuthRedirectPath } from '../../shared/authRedirect';
import { Actions } from '../actions';
import type { User } from '../model';

type ComponentWithAuthProps = Readonly<{
  user: User | null;
  isUserReady: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
}>;

type OwnProps = Readonly<{
  children: (props: ComponentWithAuthProps) => ReactNode;
}>;

export default function WithAuth({ children }: OwnProps) {
  const dispatch = useDispatch();
  const store = useStore();
  const authDispatched = useRef(false);

  // BIS-3204: read the auth slice through useSyncExternalStore instead of
  // useSelector.
  //
  // react-redux 7.2.2 predates concurrent rendering, and under React 19 its
  // subscription does not reliably deliver updates to this component. Auth is
  // the one slice where that is fatal rather than cosmetic: SIGNED_IN is the
  // only dispatch gating first render, so a single missed notification leaves
  // Root on <AppLoading /> until the user refreshes. index.tsx dispatches
  // checkingAuth at module level to try to win the race before React mounts,
  // but on a slow auth response the epic resolves mid-mount and the update is
  // lost.
  //
  // useSyncExternalStore is what react-redux v9 is built on and what React
  // guarantees against tearing. Using it directly here fixes this component
  // without a repo-wide react-redux upgrade (static/ld pins 7.2.2 too). The
  // getSnapshot below is reference-stable: the auth reducer returns the
  // existing state object for unrelated actions.
  //
  // Collapse this back to useSelector once react-redux is on v9.
  const subscribe = useCallback((onChange: () => void) => store.subscribe(onChange), [store]);
  const getAuthSnapshot = useCallback(() => (store.getState() as AppState).auth, [store]);
  const { user, error, isReady } = useSyncExternalStore(subscribe, getAuthSnapshot, getAuthSnapshot);

  const typedError = error as Error | null;
  const userReady = isReady as boolean;

  const signIn = () => dispatch(Actions.signIn());
  const signOut = () => dispatch(Actions.signOut());

  useEffect(() => {
    const redirectPath = sessionStorage.getItem('oauthRedirectPath');
    if (redirectPath !== null) {
      sessionStorage.removeItem('oauthRedirectPath');
      const currentPath = getCurrentAuthRedirectPath();
      const normalizedRedirectPath = normalizeAuthRedirectPath(redirectPath);

      // BIS-2630: avoid replacing the page with itself when stale auth redirect
      // state survives the login round-trip.
      if (normalizedRedirectPath !== currentPath) {
        window.location.replace(normalizedRedirectPath);
        return;
      }
    }
    // Only dispatch once on mount; skip if auth already resolved
    // (dispatched early from index.tsx) or if we already dispatched.
    if (authDispatched.current) {
      return;
    }
    authDispatched.current = true;
    // Read the store rather than the rendered value: a stale `false` here used
    // to re-dispatch checkingAuth, which resets isReady and discards auth that
    // had already resolved.
    if (!(store.getState() as AppState).auth.isReady) {
      dispatch(Actions.checkingAuth());
    }
  }, [dispatch, store]);

  const wrappedProps = {
    user,
    isUserReady: userReady,
    onSignIn: signIn,
    onSignOut: signOut,
  };

  if (typedError) {
    return <RedBox error={Error(typedError.message)} />;
  }

  return <>{children(wrappedProps)}</>;
}
